import { useState, useMemo } from 'react';
import { toast } from 'sonner';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '@openzeppelin/ui-components';
import { Icon } from '@ascend/shared/components';
import {
  PaginationControls,
  SortableHeader,
  type SortDirection,
  usePaginatedRows,
  useTableControls,
} from '../components/TableControls';

// ─── Types ────────────────────────────────────────────────────────────────────

type ReportType =
  | 'investor_activity'
  | 'compliance_actions'
  | 'kyc_submissions'
  | 'liquidation_events'
  | 'identity_changes'
  | 'facility_operations';

interface ReportRow {
  id: string;
  date: string;
  type: string;
  actor: string;
  target: string;
  action: string;
  details: string;
  status: string;
}

type ReportSortKey = 'date' | 'type' | 'actor' | 'target' | 'action' | 'details' | 'status';

// ─── Constants ────────────────────────────────────────────────────────────────

const REPORT_TYPES: { value: ReportType; label: string; description: string }[] = [
  { value: 'investor_activity', label: 'Investor Activity', description: 'Deposits, withdrawals, and position changes' },
  { value: 'compliance_actions', label: 'Compliance Actions', description: 'Freezes, pauses, clawbacks, and encumbrances' },
  { value: 'kyc_submissions', label: 'KYC Submissions', description: 'KYC reviews, approvals, and rejections' },
  { value: 'liquidation_events', label: 'Liquidation Events', description: 'Manual and automated liquidations' },
  { value: 'identity_changes', label: 'Identity Changes', description: 'Claim additions, removals, and status changes' },
  { value: 'facility_operations', label: 'Facility Operations', description: 'Facility deployments, config changes, and shutdowns' },
];

const PERIOD_PRESETS: { label: string; days: number }[] = [
  { label: 'Last 7 days', days: 7 },
  { label: 'Last 30 days', days: 30 },
  { label: 'Last 90 days', days: 90 },
  { label: 'Year to date', days: 365 },
];

const STATUS_STYLES: Record<string, string> = {
  completed: 'bg-green-100 text-green-700',
  pending: 'bg-yellow-100 text-yellow-700',
  failed: 'bg-red-100 text-red-700',
  executed: 'bg-green-100 text-green-700',
  reverted: 'bg-gray-100 text-gray-600',
};

const INPUT_CLASS =
  'w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2';

// ─── Mock Data Generator ──────────────────────────────────────────────────────

function generateMockData(types: ReportType[], from: string, to: string): ReportRow[] {
  const rows: ReportRow[] = [];
  const fromDate = new Date(from);
  const toDate = new Date(to);

  const templates: Record<ReportType, Array<Omit<ReportRow, 'id' | 'date'>>> = {
    investor_activity: [
      { type: 'Investor Activity', actor: 'user-1', target: 'USDC Core Facility', action: 'Deposit', details: '$50,000 USDC deposited', status: 'completed' },
      { type: 'Investor Activity', actor: 'user-2', target: 'ABN Yield Facility', action: 'Withdrawal', details: '$15,000 USDC withdrawn', status: 'completed' },
      { type: 'Investor Activity', actor: 'user-3', target: 'SNG Stable Facility', action: 'Borrow', details: '$10,000 USDC borrowed at 3.8%', status: 'completed' },
      { type: 'Investor Activity', actor: 'user-1', target: 'USDC Core Facility', action: 'Repayment', details: '$5,000 USDC repaid', status: 'completed' },
      { type: 'Investor Activity', actor: 'user-5', target: 'ABN Yield Facility', action: 'Deposit', details: '$25,000 USDC deposited', status: 'pending' },
    ],
    compliance_actions: [
      { type: 'Compliance', actor: 'admin-1', target: 'facility-4', action: 'Pause', details: 'High volatility in underlying asset', status: 'executed' },
      { type: 'Compliance', actor: 'admin-1', target: 'user-4', action: 'Freeze', details: 'Suspicious transaction pattern flagged', status: 'executed' },
      { type: 'Compliance', actor: 'admin-2', target: 'facility-2', action: 'Unpause', details: 'Maintenance window completed', status: 'executed' },
      { type: 'Compliance', actor: 'admin-1', target: 'user-2', action: 'Clawback', details: '500 USDC clawback - regulatory requirement', status: 'executed' },
      { type: 'Compliance', actor: 'admin-2', target: 'user-3', action: 'Encumber', details: '1,000 AGF encumbered - pending investigation', status: 'pending' },
    ],
    kyc_submissions: [
      { type: 'KYC', actor: 'Bob Smith', target: 'rev-001', action: 'Submitted', details: 'Individual - Onfido verification', status: 'completed' },
      { type: 'KYC', actor: 'Sarah Connor', target: 'rev-002', action: 'Submitted', details: 'Individual - Sumsub verification', status: 'pending' },
      { type: 'KYC', actor: 'admin-1', target: 'rev-001', action: 'Approved', details: 'Risk score 15/100 - all docs verified', status: 'completed' },
      { type: 'KYC', actor: 'admin-1', target: 'rev-004', action: 'Rejected', details: 'Expired passport - risk score 95/100', status: 'failed' },
      { type: 'KYC', actor: 'MegaCorp Inc', target: 'rev-003', action: 'Submitted', details: 'Corporate - Onfido verification', status: 'completed' },
    ],
    liquidation_events: [
      { type: 'Liquidation', actor: 'system', target: 'position-1', action: 'Auto-Liquidation', details: 'Health ratio dropped to 1.05 - $10,000 collateral seized', status: 'executed' },
      { type: 'Liquidation', actor: 'admin-1', target: 'position-3', action: 'Manual Liquidation', details: 'Health ratio 1.20 - $3,000 collateral seized', status: 'executed' },
      { type: 'Liquidation', actor: 'system', target: 'position-6', action: 'Auto-Liquidation', details: 'Health ratio dropped to 0.98 - full liquidation', status: 'executed' },
      { type: 'Liquidation', actor: 'system', target: 'position-5', action: 'Liquidation Warning', details: 'Health ratio approaching threshold at 1.25', status: 'pending' },
    ],
    identity_changes: [
      { type: 'Identity', actor: 'admin-1', target: 'user-1', action: 'Claim Added', details: 'ACCREDITED_INVESTOR claim added', status: 'completed' },
      { type: 'Identity', actor: 'admin-2', target: 'user-3', action: 'Claim Removed', details: 'KYC_PENDING claim removed after verification', status: 'completed' },
      { type: 'Identity', actor: 'admin-1', target: 'user-4', action: 'Status Changed', details: 'Status changed from active to frozen', status: 'executed' },
      { type: 'Identity', actor: 'admin-1', target: 'user-5', action: 'Identity Created', details: 'New on-chain identity deployed', status: 'completed' },
    ],
    facility_operations: [
      { type: 'Facility Ops', actor: 'admin-1', target: 'facility-1', action: 'Config Update', details: 'Interest rate changed from 5.0% to 5.5%', status: 'completed' },
      { type: 'Facility Ops', actor: 'admin-2', target: 'facility-4', action: 'Deployment', details: 'High Risk Alt Facility deployed with UNI collateral', status: 'completed' },
      { type: 'Facility Ops', actor: 'admin-1', target: 'facility-3', action: 'Threshold Update', details: 'Liquidation threshold changed from 120% to 110%', status: 'completed' },
      { type: 'Facility Ops', actor: 'admin-1', target: 'facility-2', action: 'Oracle Update', details: 'Price feed oracle updated to Chainlink v3', status: 'completed' },
    ],
  };

  let rowIdx = 0;
  for (const type of types) {
    const items = templates[type];
    for (const item of items) {
      const daySpread = Math.max(1, (toDate.getTime() - fromDate.getTime()) / (1000 * 60 * 60 * 24));
      const randomOffset = Math.floor(Math.random() * daySpread);
      const rowDate = new Date(fromDate);
      rowDate.setDate(rowDate.getDate() + randomOffset);
      if (rowDate > toDate) rowDate.setTime(toDate.getTime());

      rows.push({
        ...item,
        id: `row-${rowIdx++}`,
        date: rowDate.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }),
      });
    }
  }

  rows.sort((a, b) => {
    const [dA, mA, yA] = a.date.split('/').map(Number);
    const [dB, mB, yB] = b.date.split('/').map(Number);
    return new Date(yB, mB - 1, dB).getTime() - new Date(yA, mA - 1, dA).getTime();
  });

  return rows;
}

function formatDateForInput(date: Date): string {
  return date.toISOString().split('T')[0];
}

function parseDayMonthYear(value: string): number {
  const [day, month, year] = value.split('/').map((part) => Number(part));
  return new Date(year, month - 1, day).getTime();
}

function rowToCSV(rows: ReportRow[]): string {
  const headers = ['Date', 'Type', 'Actor', 'Target', 'Action', 'Details', 'Status'];
  const csvRows = [headers.join(',')];
  for (const row of rows) {
    csvRows.push(
      [row.date, row.type, row.actor, row.target, row.action, `"${row.details}"`, row.status].join(','),
    );
  }
  return csvRows.join('\n');
}

// ─── Page Component ───────────────────────────────────────────────────────────

export function AuditPage() {
  const today = new Date();
  const thirtyDaysAgo = new Date(today);
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

  const [selectedTypes, setSelectedTypes] = useState<ReportType[]>(['compliance_actions']);
  const [dateFrom, setDateFrom] = useState(formatDateForInput(thirtyDaysAgo));
  const [dateTo, setDateTo] = useState(formatDateForInput(today));
  const [reportData, setReportData] = useState<ReportRow[] | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [sortKey, setSortKey] = useState<ReportSortKey>('date');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const {
    page: resultsPage,
    setPage: setResultsPage,
    pageSize: resultsPageSize,
    setPageSize: setResultsPageSize,
    resetPage: resetResultsPage,
  } = useTableControls();

  const selectedCount = selectedTypes.length;

  function toggleType(type: ReportType) {
    setSelectedTypes((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type],
    );
  }

  function applyPreset(days: number) {
    const to = new Date();
    const from = new Date(to);
    from.setDate(from.getDate() - days);
    setDateFrom(formatDateForInput(from));
    setDateTo(formatDateForInput(to));
  }

  async function handleGenerate() {
    if (selectedTypes.length === 0) {
      toast.error('Select at least one report type');
      return;
    }
    if (!dateFrom || !dateTo) {
      toast.error('Select a date range');
      return;
    }
    if (new Date(dateFrom) > new Date(dateTo)) {
      toast.error('Start date must be before end date');
      return;
    }

    setIsGenerating(true);
    await new Promise((r) => setTimeout(r, 1500));
    const data = generateMockData(selectedTypes, dateFrom, dateTo);
    setReportData(data);
    setIsGenerating(false);
    resetResultsPage();
    toast.success(`Report generated - ${data.length} records found`);
  }

  function handleExportCSV() {
    if (!reportData || reportData.length === 0) return;

    const csv = rowToCSV(reportData);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `audit-report-${dateFrom}-to-${dateTo}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    toast.success('CSV exported successfully');
  }

  const typeSummary = useMemo(() => {
    if (!reportData) return {};
    const counts: Record<string, number> = {};
    for (const row of reportData) {
      counts[row.type] = (counts[row.type] || 0) + 1;
    }
    return counts;
  }, [reportData]);

  const rows = useMemo(() => reportData ?? [], [reportData]);
  const sortedRows = useMemo(() => {
    return [...rows].sort((a, b) => {
      let comparison = 0;

      switch (sortKey) {
        case 'date':
          comparison = parseDayMonthYear(a.date) - parseDayMonthYear(b.date);
          break;
        case 'type':
          comparison = a.type.localeCompare(b.type);
          break;
        case 'actor':
          comparison = a.actor.localeCompare(b.actor);
          break;
        case 'target':
          comparison = a.target.localeCompare(b.target);
          break;
        case 'action':
          comparison = a.action.localeCompare(b.action);
          break;
        case 'details':
          comparison = a.details.localeCompare(b.details);
          break;
        case 'status':
          comparison = a.status.localeCompare(b.status);
          break;
      }

      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [rows, sortDirection, sortKey]);
  const { rows: paginatedRows } = usePaginatedRows(sortedRows, resultsPage, resultsPageSize);

  function toggleSort(nextKey: ReportSortKey) {
    setSortDirection((prevDirection) =>
      sortKey === nextKey ? (prevDirection === 'asc' ? 'desc' : 'asc') : 'asc',
    );
    setSortKey(nextKey);
    resetResultsPage();
  }

  return (
    <div className="space-y-6 sm:space-y-8">
      {/* Page header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-start gap-3">
          <Icon name="streamline:money-graph-bar-product-data-bars-analysis-analytics-graph-business-chart" className="mt-1 h-6 w-6 sm:h-7 sm:w-7 text-foreground flex-shrink-0" />
          <div>
            <h1 className="text-xl sm:text-2xl font-bold">Audit Reports</h1>
            <p className="text-sm text-muted-foreground">
              Generate, preview, and export audit reports across platform activity.
            </p>
          </div>
        </div>
      </div>

      {/* Report Configuration */}
      <Card>
        <CardHeader className="p-6">
          <CardTitle className="flex items-center gap-2">
            <Icon name="streamline:filter-2" className="h-5 w-5" />
            Report Configuration
          </CardTitle>
          <CardDescription>
            Select the report types and date range, then generate to preview.
          </CardDescription>
        </CardHeader>
        <CardContent className="p-6">
          <div className="space-y-6">
            {/* Report types */}
            <div>
              <label className="block text-sm font-medium mb-3">
                Report Types
                <span className="ml-2 text-xs text-muted-foreground font-normal">
                  ({selectedCount} selected)
                </span>
              </label>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
                {REPORT_TYPES.map((rt) => {
                  const isSelected = selectedTypes.includes(rt.value);
                  return (
                    <button
                      key={rt.value}
                      onClick={() => toggleType(rt.value)}
                      className={`flex flex-col items-start rounded-lg border p-3 text-left transition-colors ${
                        isSelected
                          ? 'border-primary bg-primary/5 ring-1 ring-primary'
                          : 'border-border hover:bg-accent'
                      }`}
                    >
                      <span className="text-sm font-medium">{rt.label}</span>
                      <span className="text-xs text-muted-foreground mt-0.5">{rt.description}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Date range */}
            <div>
              <label className="block text-sm font-medium mb-3">
                <Icon name="streamline:blank-calendar" className="inline h-4 w-4 mr-1.5 -mt-0.5" />
                Date Range
              </label>

              {/* Period presets */}
              <div className="flex flex-wrap gap-2 mb-3">
                {PERIOD_PRESETS.map((preset) => (
                  <button
                    key={preset.label}
                    onClick={() => applyPreset(preset.days)}
                    className="rounded-full border border-border px-3 py-1 text-xs font-medium text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
                  >
                    {preset.label}
                  </button>
                ))}
              </div>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <label className="block text-xs text-muted-foreground mb-1">From</label>
                  <input
                    type="date"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                    className={INPUT_CLASS}
                  />
                </div>
                <div>
                  <label className="block text-xs text-muted-foreground mb-1">To</label>
                  <input
                    type="date"
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                    className={INPUT_CLASS}
                  />
                </div>
              </div>
            </div>

            {/* Generate button */}
            <div className="flex justify-end">
              <Button
                disabled={isGenerating || selectedTypes.length === 0}
                onClick={handleGenerate}
              >
                {isGenerating ? (
                  <>
                    <Icon name="streamline:interface-page-controller-loading-1-progress-loading-load-wait-waiting" className="h-4 w-4 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Icon name="streamline:interface-file-text-text-common-file" className="h-4 w-4 mr-2" />
                    Generate Report
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Report Results */}
      {reportData !== null && (
        <Card>
          <CardHeader className="p-6">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <CardTitle>Report Results</CardTitle>
                <CardDescription>
                  {reportData.length} records from {dateFrom} to {dateTo}
                </CardDescription>
              </div>
              {reportData.length > 0 && (
                <Button variant="outline" size="sm" onClick={handleExportCSV}>
                  <Icon name="streamline:download-file" className="h-4 w-4 mr-2" />
                  Download CSV
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent className="p-6">
            {/* Type summary badges */}
            {Object.keys(typeSummary).length > 0 && (
              <div className="flex flex-wrap gap-2 mb-4">
                {Object.entries(typeSummary).map(([type, count]) => (
                  <span
                    key={type}
                    className="rounded-full border border-border px-3 py-1 text-xs font-medium text-muted-foreground"
                  >
                    {type}: {count}
                  </span>
                ))}
              </div>
            )}

            {reportData.length === 0 ? (
              <div className="rounded-xl border-2 border-dashed border-border p-8 text-center">
                <Icon name="streamline:money-graph-bar-product-data-bars-analysis-analytics-graph-business-chart" className="mx-auto h-8 w-8 text-muted-foreground/40 mb-3" />
                <p className="text-muted-foreground font-medium">
                  No records found for the selected criteria.
                </p>
                <p className="text-sm text-muted-foreground mt-1">
                  Try adjusting your date range or report types.
                </p>
              </div>
            ) : (
              <>
                {/* Desktop table */}
                <div className="hidden md:block overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border text-left">
                        <SortableHeader<ReportSortKey>
                          label="Date"
                          sortKey="date"
                          activeSortKey={sortKey}
                          direction={sortDirection}
                          onSort={toggleSort}
                        />
                        <SortableHeader<ReportSortKey>
                          label="Type"
                          sortKey="type"
                          activeSortKey={sortKey}
                          direction={sortDirection}
                          onSort={toggleSort}
                        />
                        <SortableHeader<ReportSortKey>
                          label="Actor"
                          sortKey="actor"
                          activeSortKey={sortKey}
                          direction={sortDirection}
                          onSort={toggleSort}
                        />
                        <SortableHeader<ReportSortKey>
                          label="Target"
                          sortKey="target"
                          activeSortKey={sortKey}
                          direction={sortDirection}
                          onSort={toggleSort}
                        />
                        <SortableHeader<ReportSortKey>
                          label="Action"
                          sortKey="action"
                          activeSortKey={sortKey}
                          direction={sortDirection}
                          onSort={toggleSort}
                        />
                        <SortableHeader<ReportSortKey>
                          label="Details"
                          sortKey="details"
                          activeSortKey={sortKey}
                          direction={sortDirection}
                          onSort={toggleSort}
                        />
                        <SortableHeader<ReportSortKey>
                          label="Status"
                          sortKey="status"
                          activeSortKey={sortKey}
                          direction={sortDirection}
                          onSort={toggleSort}
                        />
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {paginatedRows.map((row) => (
                        <tr key={row.id} className="hover:bg-accent/50 transition-colors">
                          <td className="py-3 pr-4 whitespace-nowrap font-mono text-xs">{row.date}</td>
                          <td className="py-3 pr-4 whitespace-nowrap">{row.type}</td>
                          <td className="py-3 pr-4 whitespace-nowrap font-mono text-xs">{row.actor}</td>
                          <td className="py-3 pr-4 whitespace-nowrap font-mono text-xs">{row.target}</td>
                          <td className="py-3 pr-4 whitespace-nowrap font-medium">{row.action}</td>
                          <td className="py-3 pr-4 text-muted-foreground max-w-[300px] truncate">{row.details}</td>
                          <td className="py-3">
                            <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_STYLES[row.status] ?? 'bg-gray-100 text-gray-600'}`}>
                              {row.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Mobile cards */}
                <div className="md:hidden space-y-3">
                  {paginatedRows.map((row) => (
                    <div key={row.id} className="rounded-lg border border-border p-3 space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="font-mono text-xs text-muted-foreground">{row.date}</span>
                        <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_STYLES[row.status] ?? 'bg-gray-100 text-gray-600'}`}>
                          {row.status}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-muted-foreground">{row.type}</span>
                        <span className="text-sm font-medium">{row.action}</span>
                      </div>
                      <p className="text-sm text-muted-foreground">{row.details}</p>
                      <div className="flex gap-4 text-xs text-muted-foreground">
                        <span>Actor: <span className="font-mono">{row.actor}</span></span>
                        <span>Target: <span className="font-mono">{row.target}</span></span>
                      </div>
                    </div>
                  ))}
                </div>

                <PaginationControls
                  page={resultsPage}
                  setPage={setResultsPage}
                  pageSize={resultsPageSize}
                  setPageSize={setResultsPageSize}
                  totalItems={sortedRows.length}
                  label="report rows"
                />
              </>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
